/**
 * "Create a contact" for an enquiry — find or attach the company, check the
 * company's contacts, then open the ordinary contact form prefilled.
 *
 * Owner's decisions, 2026-10-02: every Enquiries row offers Create a contact.
 * It opens the existing contact form PREFILLED from the enquiry with the company
 * LOCKED. On a row with no company it first runs the existing find-or-create
 * company step and LINKS the enquiry to the result, then opens the form.
 *
 * ⚠️ THE LINK HAPPENS BEFORE THE CONTACT, deliberately. The enquiry row shows
 * the company the moment it is attached, so a person who cancels the contact
 * form still leaves the row pointing at the account they chose — and
 * `onLinked` fires there, not after the contact saves.
 *
 * ⚠️ THE DUPLICATE CHECK IS A CONVENIENCE, NOT A GUARANTEE. Contact email is not
 * unique server-side, so the company's contacts are read by email before the
 * form opens; a match shows "already a contact" instead. A FAILED read goes on
 * to the form anyway — a request that did not come back is not evidence the
 * person is on file, and refusing on it would block the action outright. No
 * address on the enquiry skips the check.
 *
 * ⚠️ It does not close itself after a save: `onCreated` is the end of the flow,
 * and the caller closes it there.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { ContactFormModal, type ContactPrefill } from '@/components/contacts/ContactFormModal';
import {
  CreateCompanyForEnquiry,
  type EnquiryCompanySeed,
} from '@/components/enquiries/CreateCompanyForEnquiry';
import { Button, Modal, Skeleton } from '@/components/ui';
import { EmailLink } from '@/components/ui/TextLink';
import { findCompanyContactByEmail, isCheckableEmail } from '@/lib/enquiry-contact';
import type { Contact } from '@/lib/types';

export interface EnquiryContactRequest {
  company: { id: number; name: string } | null;
  companySeed: EnquiryCompanySeed;
  prefill: ContactPrefill;
}

export interface CreateContactForEnquiryProps {
  open: boolean;
  request: EnquiryContactRequest | null;
  /** Any cancel or close, at any step. */
  onClose: () => void;
  /** Persist the enquiry → company link; throws on failure. */
  link: (companyId: number) => Promise<void>;
  /** Called once the link landed. */
  onLinked: (company: { id: number; name: string }) => void;
  /** The contact saved; the caller closes. */
  onCreated: (contact: Contact) => void;
}

type CompanyRef = { id: number; name: string };

type Step =
  | { name: 'company' }
  | { name: 'checking'; company: CompanyRef }
  | { name: 'onFile'; company: CompanyRef; contact: Contact }
  | { name: 'form'; company: CompanyRef };

/* One key per request OBJECT, so a new request remounts the flow and no step
   from one enquiry survives into the next. */
const requestKeys = new WeakMap<EnquiryContactRequest, number>();
let nextRequestKey = 0;
function keyOf(request: EnquiryContactRequest): number {
  let key = requestKeys.get(request);
  if (key === undefined) {
    nextRequestKey += 1;
    key = nextRequestKey;
    requestKeys.set(request, key);
  }
  return key;
}

export function CreateContactForEnquiry({
  open,
  request,
  onClose,
  link,
  onLinked,
  onCreated,
}: CreateContactForEnquiryProps) {
  if (!open || request === null) return null;
  return (
    <EnquiryContactFlow
      key={keyOf(request)}
      request={request}
      onClose={onClose}
      link={link}
      onLinked={onLinked}
      onCreated={onCreated}
    />
  );
}

function EnquiryContactFlow({
  request,
  onClose,
  link,
  onLinked,
  onCreated,
}: {
  request: EnquiryContactRequest;
  onClose: () => void;
  link: (companyId: number) => Promise<void>;
  onLinked: (company: CompanyRef) => void;
  onCreated: (contact: Contact) => void;
}) {
  const [step, setStep] = useState<Step>(() =>
    request.company === null
      ? { name: 'company' }
      : { name: 'checking', company: request.company },
  );

  const email = request.prefill.email;
  const checkingCompany = step.name === 'checking' ? step.company : null;

  useEffect(() => {
    if (checkingCompany === null) return;
    if (!isCheckableEmail(email)) {
      setStep({ name: 'form', company: checkingCompany });
      return;
    }
    const controller = new AbortController();
    findCompanyContactByEmail(checkingCompany.id, email, controller.signal)
      .then((found) => {
        if (controller.signal.aborted) return;
        setStep(
          found === null
            ? { name: 'form', company: checkingCompany }
            : { name: 'onFile', company: checkingCompany, contact: found },
        );
      })
      .catch(() => {
        // A failed check is not evidence either way — the form opens regardless.
        if (!controller.signal.aborted) setStep({ name: 'form', company: checkingCompany });
      });
    return () => controller.abort();
  }, [checkingCompany, email]);

  if (step.name === 'company') {
    return (
      <CreateCompanyForEnquiry
        open
        seed={request.companySeed}
        createdContinueLabel="Continue to contact"
        onClose={onClose}
        link={link}
        onLinked={(company) => {
          onLinked(company);
          setStep({ name: 'checking', company });
        }}
      />
    );
  }

  if (step.name === 'checking') {
    return (
      <Modal
        open
        onClose={onClose}
        title={`Create a contact at ${step.company.name}`}
        footer={
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        }
      >
        <div className="space-y-2" aria-busy="true">
          <p role="status" className="text-xs text-fg-muted">
            Checking contacts…
          </p>
          <Skeleton className="h-10 w-full" />
        </div>
      </Modal>
    );
  }

  if (step.name === 'onFile') {
    const { company, contact } = step;
    const address = contact.email ?? contact.secondaryEmail ?? email ?? '';
    const who = contact.name.trim() || address;
    return (
      <Modal
        open
        onClose={onClose}
        title="Already a contact"
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
            {/* A real Link: `Button href` would reload the whole bundle. */}
            <Link
              to={`/contacts/${contact.id}`}
              onClick={onClose}
              className="inline-flex h-9 items-center justify-center rounded-md bg-accent-brand px-3 text-sm font-medium text-fg-on-accent transition-colors duration-150 ease-out hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring motion-reduce:transition-none max-sm:h-11"
            >
              Open contact
            </Link>
          </>
        }
      >
        <p className="text-sm text-fg">
          {address !== '' && who === address ? (
            <EmailLink address={address} className="font-medium" />
          ) : (
            <span className="font-medium">{who}</span>
          )}
          {address !== '' && who !== address ? (
            <>
              {' ('}
              <EmailLink address={address} />
              {')'}
            </>
          ) : null}{' '}
          is already a contact at{' '}
          <span className="font-medium">{company.name}</span>.
        </p>
      </Modal>
    );
  }

  return (
    <ContactFormModal
      open
      onClose={onClose}
      lockedCompany={step.company}
      prefill={request.prefill}
      onSaved={onCreated}
    />
  );
}
